import React, { useRef, useState } from "react";
import axios from "axios";
import "./ContactUs.css";

const fallbackError = "Vi kunde inte bekräfta att meddelandet togs emot. Försök igen eller kontakta oss via e-post.";

const createRequestId = () => {
  if (typeof window.crypto?.randomUUID === "function") {
    return window.crypto.randomUUID();
  }

  // getRandomValues also works where randomUUID is unavailable, such as local HTTP.
  if (typeof window.crypto?.getRandomValues !== "function") {
    throw new Error("Secure request identifiers are unavailable");
  }
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const ContactUs = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const submissionInProgress = useRef(false);
  const pendingRequest = useRef(null);

  const updateField = (setter) => (event) => {
    setter(event.target.value);
    pendingRequest.current = null;
    setFeedback(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submissionInProgress.current) return;

    const content = {
      name: name.trim(),
      email: email.trim(),
      message: message.trim(),
      website: website.trim(),
    };
    if (!content.name || !content.email || !content.message) {
      setFeedback({ type: "error", message: "Fyll i namn, e-postadress och meddelande." });
      return;
    }

    submissionInProgress.current = true;
    setFeedback(null);
    setIsLoading(true);
    try {
      const fingerprint = JSON.stringify(content);
      if (pendingRequest.current?.fingerprint !== fingerprint) {
        pendingRequest.current = { fingerprint, requestId: createRequestId() };
      }
      const response = await axios.post(
        "/api/contact",
        { ...content, requestId: pendingRequest.current.requestId },
        { timeout: 20000, headers: { "Content-Type": "application/json" } }
      );
      if (response?.data?.ok !== true || typeof response.data.id !== "string" || !response.data.id.trim()) {
        throw new Error("The contact service did not confirm acceptance");
      }
      setFeedback({ type: "success", message: "Tack! Ditt meddelande har tagits emot." });
      setName("");
      setEmail("");
      setMessage("");
      setWebsite("");
      pendingRequest.current = null;
    } catch (error) {
      const serverMessage = error?.response?.data?.message;
      const validMessage = typeof serverMessage === "string"
        && serverMessage.trim().length > 0
        && serverMessage.trim().length <= 300;
      setFeedback({ type: "error", message: validMessage ? serverMessage.trim() : fallbackError });
    } finally {
      submissionInProgress.current = false;
      setIsLoading(false);
    }
  };
  return (
    <section className="thas-contact" aria-labelledby="contact-heading">
      <div className="thas-contact-inner">
        <div className="thas-contact-intro">
          <p className="thas-contact-eyebrow">KONTAKTA OSS</p>
          <h2 id="contact-heading">Låt oss prata om<br />ditt företag.</h2>
          <p className="thas-contact-description">
            Behöver du hjälp med bokföringen eller vill du veta mer om våra
            tjänster? Berätta vad du behöver, så tar vi nästa steg tillsammans.
          </p>
          <div className="thas-contact-methods">
            <a href="mailto:info@thas-redovisning.se">
              <span className="thas-contact-icon" aria-hidden="true">@</span>
              <span><span className="thas-contact-label">E-post</span>info@thas-redovisning.se</span>
              <span className="thas-contact-arrow" aria-hidden="true">↗</span>
            </a>
            <a href="tel:+46760211685">
              <span className="thas-contact-icon" aria-hidden="true">↗</span>
              <span><span className="thas-contact-label">Telefon</span>+46 760211685</span>
              <span className="thas-contact-arrow" aria-hidden="true">↗</span>
            </a>
          </div>
          <p className="thas-contact-location">Stockholm, Sverige</p>
        </div>

        <form className="thas-contact-form" method="post" action="/api/contact" onSubmit={handleSubmit} aria-busy={isLoading} aria-labelledby="contact-form-heading">
          <h3 id="contact-form-heading">Hur kan vi hjälpa dig?</h3>
          <p className="thas-contact-form-note">Alla fält behöver fyllas i.</p>
          <fieldset disabled={isLoading}>
            <div className="thas-contact-honeypot" aria-hidden="true">
              <label htmlFor="contact-website">Lämna detta fält tomt</label>
              <input
                id="contact-website"
                name="website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={website}
                onChange={updateField(setWebsite)}
              />
            </div>
            <div className="thas-contact-field">
              <label htmlFor="contact-name">Namn</label>
              <input
                id="contact-name"
                name="name"
                type="text"
                autoComplete="name"
                placeholder="Ditt namn"
                required
                maxLength={150}
                value={name}
                onChange={updateField(setName)}
              />
            </div>
            <div className="thas-contact-field">
              <label htmlFor="contact-email">E-postadress</label>
              <input
                id="contact-email"
                name="email"
                type="email"
                autoComplete="email"
                placeholder="namn@foretag.se"
                required
                maxLength={254}
                value={email}
                onChange={updateField(setEmail)}
              />
            </div>
            <div className="thas-contact-field">
              <label htmlFor="contact-message">Meddelande</label>
              <textarea
                id="contact-message"
                name="message"
                placeholder="Berätta kort om ditt företag och vad du behöver hjälp med."
                required
                rows={4}
                maxLength={10000}
                value={message}
                onChange={updateField(setMessage)}
              />
            </div>
            <button className="thas-contact-submit" type="submit">
              {isLoading ? "Skickar meddelande…" : "Skicka meddelande"}
              <span aria-hidden="true">↗</span>
            </button>
          </fieldset>
          {feedback && (
            <p
              className={`thas-contact-feedback thas-contact-feedback-${feedback.type}`}
              role={feedback.type === "error" ? "alert" : "status"}
            >
              {feedback.message}
              {feedback.type === "error" && (
                <> <a href="mailto:info@thas-redovisning.se">Mejla info@thas-redovisning.se</a></>
              )}
            </p>
          )}
        </form>
      </div>
    </section>
  );
};

export default ContactUs;
